import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { useAuth } from '../../contexts/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, currentUser, userProfile, error: authError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser && userProfile) {
      if (userProfile.role === 'editor') {
        navigate('/content-approval', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [currentUser, userProfile, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {
      await signIn(formData.email, formData.password, formData.rememberMe);
      // Navigation is handled by the useEffect once userProfile is loaded
    } catch (err) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen relative overflow-hidden bg-gradient-to-br from-darkNavy to-[#1e3a5f] lg:bg-none lg:bg-gray-50">
      {/* Mobile Decorative Circles */}
      <div className="absolute w-72 h-72 bg-blue-500/20 rounded-full blur-3xl -top-20 -left-20 lg:hidden animate-pulse"></div>
      <div className="absolute w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl -bottom-32 -right-32 lg:hidden animate-pulse" style={{ animationDelay: '1s' }}></div>

      {/* Left Panel - Dark Navy with Logo (Desktop Only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-darkNavy to-[#1e3a5f] relative overflow-hidden shadow-2xl z-10">
        {/* Decorative circles */}
        <div className="absolute w-72 h-72 bg-blue-600/20 rounded-full blur-3xl -top-20 -left-20"></div>
        <div className="absolute w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl -bottom-32 -right-32"></div>
        
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-12">
          {/* Logo with glow effect */}
          <div className="mb-8 p-6 rounded-full bg-white/5 backdrop-blur-lg animate-[pulse_3s_ease-in-out_infinite] shadow-[0_0_40px_rgba(37,99,235,0.3)] border border-white/10">
            <img src={logo} alt="Smart Brain Logo" className="w-24 h-24 drop-shadow-xl" />
          </div>
          
          <h1 className="text-5xl font-bold text-white mb-4 font-poppins text-center tracking-tight drop-shadow-lg">
            Smart Brain
          </h1>
          <p className="text-xl text-blue-200 mb-12 text-center font-medium tracking-wide">
            Admin Control Center
          </p>

          {/* Features */}
          <div className="space-y-5 w-full max-w-sm">
            {[
              'Manage Users & Content',
              'Track Analytics & Revenue',
              'Control Pricing & Subscriptions'
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-4 px-6 py-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300 hover:translate-x-2 cursor-default shadow-lg">
                <div className="w-8 h-8 bg-emerald-500/20 border border-emerald-500/50 rounded-full flex items-center justify-center flex-shrink-0 shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                  <span className="text-emerald-400 text-sm font-bold">✓</span>
                </div>
                <span className="text-white/90 font-medium tracking-wide">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-8 relative z-10 lg:bg-transparent">
        <div className="w-full max-w-md">
          {/* Mobile Header */}
          <div className="lg:hidden flex flex-col items-center justify-center mb-8">
            <div className="mb-5 p-4 rounded-full bg-white/10 backdrop-blur-md shadow-[0_0_30px_rgba(37,99,235,0.2)] border border-white/20 animate-[pulse_3s_ease-in-out_infinite]">
              <img src={logo} alt="Smart Brain Logo" className="w-16 h-16 object-contain drop-shadow-lg" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2 font-poppins text-center tracking-tight drop-shadow-lg">
              Smart Brain
            </h1>
            <p className="text-base text-blue-200 text-center font-medium tracking-wide">
              Admin Control Center
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white/95 backdrop-blur-xl rounded-[2rem] shadow-2xl border border-white/50 p-8 sm:p-10 transition-all hover:shadow-[0_20px_60px_-15px_rgba(37,99,235,0.3)]">
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-darkNavy mb-2 font-poppins tracking-tight">
                Welcome Back
              </h2>
              <p className="text-gray-500 text-sm sm:text-base font-medium">Sign in to access your dashboard</p>
            </div>

            {/* Error Message */}
            {(error || authError) && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error || authError}
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="admin@smartbrain.ng"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white transition-all disabled:bg-gray-100 disabled:cursor-not-allowed placeholder:text-gray-400 shadow-sm"
                />
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-bold text-gray-700">
                    Password
                  </label>
                  <a href="#" className="text-sm text-primary font-bold hover:text-blue-700 hover:underline transition-colors">
                    Forgot password?
                  </a>
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-200 rounded-xl text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white transition-all disabled:bg-gray-100 disabled:cursor-not-allowed placeholder:text-gray-400 shadow-sm tracking-widest"
                />
              </div>

              {/* Remember Me */}
              <div className="flex items-center pt-1">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative flex items-center justify-center">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      disabled={loading}
                      className="peer appearance-none w-5 h-5 border-2 border-gray-300 rounded-lg checked:bg-primary checked:border-primary focus:ring-2 focus:ring-primary/20 focus:ring-offset-1 transition-all cursor-pointer disabled:opacity-50"
                    />
                    <svg
                      className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-600 group-hover:text-gray-900 transition-colors">Keep me signed in</span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3.5 bg-gradient-to-r from-primary to-blue-600 text-white rounded-xl font-bold text-[15px] hover:from-blue-700 hover:to-blue-800 transition-all shadow-[0_8px_20px_rgba(37,99,235,0.25)] hover:shadow-[0_10px_25px_rgba(37,99,235,0.35)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Authenticating...</span>
                  </div>
                ) : (
                  'Access Dashboard'
                )}
              </button>
            </form>
          </div>
          
          {/* Mobile Footer */}
          <div className="mt-8 text-center lg:hidden">
            <p className="text-sm text-white/50 font-medium">
              © 2026 Smart Brain. All rights reserved.
            </p>
          </div>
          
          {/* Desktop Footer */}
          <div className="hidden lg:block mt-8 text-center">
            <p className="text-sm text-gray-400 font-medium">
              © 2026 Smart Brain. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
